import { Icon } from '@iconify-icon/react';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout } from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'bot-not-responding', title: '1. Bot Not Responding' },
  { id: 'incorrect-answers', title: '2. Incorrect or Hallucinated Answers' },
  { id: 'missing-citations', title: '3. Missing Citations' },
  { id: 'slow-responses', title: '4. Slow Response Times' },
  { id: 'ingestion-failures', title: '5. Document Ingestion Failures' },
  { id: 'embed-issues', title: '6. Embed Not Loading' },
  { id: 'contact-support', title: '7. When to Contact Support' },
];

const TroubleshootingPage = () => {
  return (
    <>
      <NextSeo
        title="When Your Bot Goes Rogue: Debugging Guide"
        description="Diagnose and fix common chatbot issues. Comprehensive troubleshooting guide for API errors, hallucinations, slow responses, and more."
        canonical="https://chat.co/docs/troubleshooting"
        openGraph={{
          url: 'https://chat.co/docs/troubleshooting',
          title: 'Chatbot Debugging Guide | Chat.co',
          description:
            'Diagnose and fix common chatbot issues. Comprehensive troubleshooting guide for API errors, hallucinations, slow responses, and more.',
          type: 'article',
          article: {
            publishedTime: '2026-01-01T00:00:00Z',
            modifiedTime: '2026-01-26T00:00:00Z',
            section: 'Troubleshooting',
            tags: ['troubleshooting', 'debugging', 'chatbot errors', 'AI issues'],
          },
          images: [
            {
              url: 'https://chat.co/images/og-docs.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co - Troubleshooting Guide',
            },
          ],
        }}
        twitter={{
          cardType: 'summary_large_image',
        }}
      />

      <DocsArticleLayout
        category="Troubleshooting"
        categoryColor="text-rose-700"
        title="When Your Bot Goes Rogue: Debugging Guide"
        description="A comprehensive guide to diagnosing and fixing common chatbot issues. Get your AI assistant back on track."
        readingTime="12 min read"
        lastUpdated="January 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'Build a RAG System',
          href: PageLink.docsBuildRagSystem,
        }}
        nextArticle={{
          title: 'Citation Quality Guide',
          href: PageLink.docsCitationQuality,
        }}
        keyTakeaways={[
          'Check API status and rate limits when bot stops responding',
          'Hallucinations usually mean missing or outdated knowledge base content',
          'Missing citations? Ensure documents are properly processed',
          'Slow responses can be improved by optimizing knowledge base size',
          'Contact support with your chatbot ID for faster resolution',
        ]}>
        {/* Introduction */}
        <p className="lead text-lg text-gray-700">
          Even the best AI systems occasionally misbehave. This guide helps you quickly diagnose and
          fix the most common issues with your Chat.co chatbot.
        </p>

        <div className="my-8 rounded-xl border border-rose-200 bg-rose-50 p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-rose-100">
              <Icon icon="tabler:stethoscope" className="text-xl text-rose-600" />
            </div>
            <div>
              <h4 className="font-semibold text-rose-900">Quick Diagnosis</h4>
              <p className="mt-1 text-rose-800">
                Before diving deep, try these quick fixes: refresh the page, clear browser cache,
                check your internet connection, and verify your API key hasn&apos;t expired.
              </p>
            </div>
          </div>
        </div>

        {/* Section 1: Bot Not Responding */}
        <section id="bot-not-responding" className="scroll-mt-24">
          <h2>1. Bot Not Responding</h2>

          <p>
            If your chatbot isn&apos;t responding at all, here&apos;s how to diagnose the issue.
          </p>

          <h3>Symptoms</h3>
          <ul>
            <li>Messages show &quot;loading&quot; indefinitely</li>
            <li>No response after sending a message</li>
            <li>Widget appears but doesn&apos;t accept input</li>
            <li>API calls return no response</li>
          </ul>

          <h3>Common Causes & Solutions</h3>

          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                  <Icon icon="tabler:key-off" className="text-lg text-red-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Invalid or Expired API Key</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Your API key may be invalid, expired, or not associated with a chatbot.
                  </p>
                  <div className="mt-3 rounded-lg bg-gray-50 p-3">
                    <p className="text-sm font-medium text-gray-700">Fix:</p>
                    <ol className="mt-1 space-y-1 text-sm text-gray-600">
                      <li>1. Go to Settings → API Keys in your dashboard</li>
                      <li>2. Verify your key hasn&apos;t expired</li>
                      <li>3. Ensure the key is assigned to a chatbot</li>
                      <li>4. Generate a new key if needed</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                  <Icon icon="tabler:clock-stop" className="text-lg text-amber-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Rate Limit Exceeded</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    You may have exceeded your message credits or hit API rate limits.
                  </p>
                  <div className="mt-3 rounded-lg bg-gray-50 p-3">
                    <p className="text-sm font-medium text-gray-700">Fix:</p>
                    <ol className="mt-1 space-y-1 text-sm text-gray-600">
                      <li>1. Check your usage in Settings → Billing</li>
                      <li>2. Wait for rate limits to reset (15 minutes)</li>
                      <li>3. Upgrade your plan if consistently hitting limits</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                  <Icon icon="tabler:snowflake" className="text-lg text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Chatbot Frozen</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    After a plan downgrade, excess chatbots may be frozen.
                  </p>
                  <div className="mt-3 rounded-lg bg-gray-50 p-3">
                    <p className="text-sm font-medium text-gray-700">Fix:</p>
                    <ol className="mt-1 space-y-1 text-sm text-gray-600">
                      <li>1. Go to your dashboard and check for frozen bots</li>
                      <li>2. Upgrade your plan to unfreeze, or</li>
                      <li>3. Delete unused bots to free up slots</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h3>API Error Codes</h3>
          <div className="my-4 overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Error Code
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Meaning
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                    INVALID_API_KEY
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Key missing or invalid
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Check/regenerate API key
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                    CHATBOT_ACCESS_DENIED
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Key not linked to bot
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Assign key to chatbot
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                    MESSAGE_LIMIT_EXCEEDED
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Monthly credits used
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Upgrade plan or wait for reset
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                    CHATBOT_FROZEN
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Bot inactive due to downgrade
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Upgrade or free up bot slots
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 2: Incorrect Answers */}
        <section id="incorrect-answers" className="scroll-mt-24">
          <h2>2. Incorrect or Hallucinated Answers</h2>

          <p>
            AI &quot;hallucinations&quot; occur when the model generates plausible-sounding but
            incorrect information. Here&apos;s how to minimize them.
          </p>

          <h3>Symptoms</h3>
          <ul>
            <li>Responses contain factually incorrect information</li>
            <li>Bot invents details not in your documents</li>
            <li>Answers don&apos;t match your knowledge base</li>
            <li>Confident-sounding but wrong responses</li>
          </ul>

          <h3>Root Causes & Solutions</h3>

          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="flex items-center gap-2 font-semibold text-gray-900">
                <Icon icon="tabler:file-off" className="text-lg text-gray-500" />
                Missing or Incomplete Knowledge Base
              </h4>
              <p className="mt-2 text-sm text-gray-600">
                If your documents don&apos;t cover a topic, the AI may try to fill gaps with its
                general knowledge, which may be wrong.
              </p>
              <div className="mt-3 rounded-lg bg-green-50 p-3">
                <p className="text-sm text-green-800">
                  <strong>Fix:</strong> Upload comprehensive documents that cover all topics your
                  bot should know about. Enable &quot;I don&apos;t know&quot; responses for topics
                  outside your KB.
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="flex items-center gap-2 font-semibold text-gray-900">
                <Icon icon="tabler:copy-off" className="text-lg text-gray-500" />
                Conflicting Information in Documents
              </h4>
              <p className="mt-2 text-sm text-gray-600">
                Multiple documents with contradictory information confuse the AI.
              </p>
              <div className="mt-3 rounded-lg bg-green-50 p-3">
                <p className="text-sm text-green-800">
                  <strong>Fix:</strong> Audit your knowledge base for outdated documents. Remove
                  duplicates and ensure information is consistent across sources.
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="flex items-center gap-2 font-semibold text-gray-900">
                <Icon icon="tabler:target-off" className="text-lg text-gray-500" />
                Poor Retrieval Accuracy
              </h4>
              <p className="mt-2 text-sm text-gray-600">
                The wrong documents are being retrieved for the user&apos;s question.
              </p>
              <div className="mt-3 rounded-lg bg-green-50 p-3">
                <p className="text-sm text-green-800">
                  <strong>Fix:</strong> Increase <code>topK</code> to retrieve more context, or
                  adjust <code>similarityThreshold</code> to filter out irrelevant matches.
                </p>
              </div>
            </div>
          </div>

          <div className="my-6 rounded-lg border-l-4 border-blue-500 bg-blue-50 p-4">
            <p className="text-sm text-blue-900">
              <strong>Pro Tip:</strong> Enable citations in your chatbot settings. When users can
              see the source of information, they can verify accuracy and trust builds naturally.
            </p>
          </div>
        </section>

        {/* Section 3: Missing Citations */}
        <section id="missing-citations" className="scroll-mt-24">
          <h2>3. Missing Citations</h2>

          <p>
            Citations are crucial for trust. If your bot isn&apos;t providing sources, here&apos;s
            how to fix it.
          </p>

          <h3>Symptoms</h3>
          <ul>
            <li>Responses have no citation links</li>
            <li>Citations array is empty in API response</li>
            <li>&quot;Show sources&quot; button doesn&apos;t appear</li>
          </ul>

          <h3>Common Causes & Solutions</h3>

          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Citations Disabled in Settings</h4>
              <p className="mt-1 text-sm text-gray-600">
                Check Appearance → Advanced → Show Citations is enabled.
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">No Relevant Documents Retrieved</h4>
              <p className="mt-1 text-sm text-gray-600">
                If the similarity threshold is too high, no documents may be retrieved.
              </p>
              <div className="mt-2 text-sm">
                <strong>Fix:</strong> Lower <code>similarityThreshold</code> from 0.8 to 0.6-0.7.
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Document Processing Incomplete</h4>
              <p className="mt-1 text-sm text-gray-600">
                Documents still being processed won&apos;t appear in citations.
              </p>
              <div className="mt-2 text-sm">
                <strong>Fix:</strong> Wait for every row under Knowledge Base &rarr; Documents to
                finish processing.
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Expert Bot Configuration Issues</h4>
              <p className="mt-1 text-sm text-gray-600">
                For expert bots using Bedrock Agents, citations require proper agent configuration.
              </p>
              <div className="mt-2 text-sm">
                <strong>Fix:</strong> Verify the Bedrock Agent is correctly associated with your
                Knowledge Base. Check that attribution is enabled in agent settings.
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Slow Responses */}
        <section id="slow-responses" className="scroll-mt-24">
          <h2>4. Slow Response Times</h2>

          <p>If your chatbot takes too long to respond, users may abandon the conversation.</p>

          <h3>Symptoms</h3>
          <ul>
            <li>Responses take more than 10 seconds</li>
            <li>First message is much slower than subsequent ones</li>
            <li>Intermittent slowdowns</li>
          </ul>

          <h3>Optimization Strategies</h3>

          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                  <Icon icon="tabler:bolt" className="text-lg text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Streaming is Enabled by Default</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Chat.co uses streaming responses by default, so users see text as it&apos;s
                    generated. If responses still feel slow, this is likely due to model or
                    knowledge base factors below.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                  <Icon icon="tabler:brain" className="text-lg text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Choose a Faster Model</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Within a model family the smaller tier answers noticeably faster. Swap the model
                    under Tune Bot &rarr; Behavior and re-test — for grounded Q&amp;A the quality
                    difference is usually smaller than you expect.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-violet-100">
                  <Icon icon="tabler:adjustments" className="text-lg text-violet-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Reduce Retrieved Context</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Lower <code>topK</code> from 10 to 5 to reduce context processing time.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                  <Icon icon="tabler:file-text" className="text-lg text-amber-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Optimize Knowledge Base</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Remove redundant documents. Smaller, focused KBs perform faster.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <h3>Expected Response Times</h3>
          <div className="my-4 overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Scenario
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Expected Time
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Simple question, streaming enabled
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    First token: &lt;1s
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Complex question, non-streaming
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">3-8 seconds</td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    First message (cold start)
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">+2-3 seconds</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 5: Ingestion Failures */}
        <section id="ingestion-failures" className="scroll-mt-24">
          <h2>5. Document Ingestion Failures</h2>

          <p>
            When documents fail to process, they won&apos;t be available for your chatbot to
            reference.
          </p>

          <h3>Symptoms</h3>
          <ul>
            <li>A failed or errored row under Knowledge Base &rarr; Documents</li>
            <li>&quot;Processing failed&quot; status</li>
            <li>Document not appearing in source count</li>
          </ul>

          <h3>Common Causes & Solutions</h3>

          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">File Too Large</h4>
              <p className="mt-1 text-sm text-gray-600">
                Maximum file size is 50MB. Split large documents into smaller files.
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Scanned PDF (Image-Only)</h4>
              <p className="mt-1 text-sm text-gray-600">
                PDFs containing only images need OCR processing. Try running OCR before upload or
                use a different format.
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Password-Protected File</h4>
              <p className="mt-1 text-sm text-gray-600">
                Remove password protection before uploading.
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Corrupted File</h4>
              <p className="mt-1 text-sm text-gray-600">
                Try opening the file locally to verify it&apos;s not corrupted. Re-export if needed.
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Unsupported Format</h4>
              <p className="mt-1 text-sm text-gray-600">
                Supported formats: PDF, DOCX, DOC, TXT, MD, HTML. Convert other formats first.
              </p>
            </div>
          </div>

          <div className="my-6 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4">
            <p className="text-sm text-amber-900">
              <strong>Tip:</strong> If a document repeatedly fails, try converting it to plain text
              (.txt) format. This bypasses most parsing issues.
            </p>
          </div>
        </section>

        {/* Section 6: Embed Issues */}
        <section id="embed-issues" className="scroll-mt-24">
          <h2>6. Embed Not Loading</h2>

          <p>If the chat widget isn&apos;t appearing on your website, check these common issues.</p>

          <h3>Symptoms</h3>
          <ul>
            <li>No chat bubble visible on website</li>
            <li>Widget frame appears but is blank</li>
            <li>Console shows JavaScript errors</li>
          </ul>

          <h3>Troubleshooting Steps</h3>

          <div className="my-6 space-y-4">
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                <span className="text-sm font-medium text-gray-600">1</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Check Embed Code Placement</h4>
                <p className="mt-1 text-sm text-gray-600">
                  The embed script should be placed just before the closing{' '}
                  <code>&lt;/body&gt;</code> tag.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                <span className="text-sm font-medium text-gray-600">2</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Verify Chatbot ID</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Ensure <code>data-bot-id</code> matches the chatbot ID shown on the bot&apos;s
                  Deployment page.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                <span className="text-sm font-medium text-gray-600">3</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Check CSP Headers</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Content Security Policy may block the widget. Allow <code>chat.co</code> in your{' '}
                  <code>script-src</code> and <code>connect-src</code> directives — the widget is
                  served from <code>chat.co/api/embed</code> and talks to the Chat.co API.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                <span className="text-sm font-medium text-gray-600">4</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Check Browser Console</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Open DevTools (F12) and check the Console tab for JavaScript errors.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                <span className="text-sm font-medium text-gray-600">5</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Test Chatbot Visibility</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Make sure the chatbot is set to &quot;Public&quot; in settings if embedding on a
                  public website.
                </p>
              </div>
            </div>
          </div>

          <h3>Correct Embed Code</h3>
          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">
              {`<!-- Place before </body> -->
<script defer src="https://chat.co/api/embed" data-bot-id="YOUR_BOT_ID"></script>`}
            </pre>
          </div>
        </section>

        {/* Section 7: Contact Support */}
        <section id="contact-support" className="scroll-mt-24">
          <h2>7. When to Contact Support</h2>

          <p>
            If you&apos;ve tried the above solutions and still have issues, our support team is here
            to help.
          </p>

          <h3>Contact Support When:</h3>
          <ul>
            <li>Issues persist after following troubleshooting steps</li>
            <li>You encounter error codes not listed above</li>
            <li>You need help with enterprise configurations</li>
            <li>You suspect a platform-wide issue</li>
            <li>You need custom integrations or features</li>
          </ul>

          <h3>How to Get Help</h3>

          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <a
              href="mailto:support@chat.co"
              className="flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
                <Icon icon="tabler:mail" className="text-2xl text-primary-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Email Support</h4>
                <p className="text-sm text-gray-600">support@chat.co</p>
                <p className="text-xs text-gray-400">Response within 24 hours</p>
              </div>
            </a>

            <Link
              href={PageLink.contactUs}
              className="flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <Icon icon="tabler:message-circle" className="text-2xl text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Contact Form</h4>
                <p className="text-sm text-gray-600">chat.co/contact-us</p>
                <p className="text-xs text-gray-400">Detailed issue reporting</p>
              </div>
            </Link>
          </div>

          <h3>Include in Your Support Request</h3>
          <ul>
            <li>Your chatbot ID and account email</li>
            <li>Steps to reproduce the issue</li>
            <li>Error messages or codes you&apos;re seeing</li>
            <li>Screenshots or screen recordings if applicable</li>
            <li>Browser and device information</li>
          </ul>

          <div className="my-8 rounded-xl border border-primary-200 bg-primary-50 p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-100">
                <Icon icon="tabler:heart" className="text-xl text-primary-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary-900">We&apos;re Here to Help</h4>
                <p className="mt-1 text-primary-800">
                  Our team is committed to your success. Don&apos;t hesitate to reach out—we&apos;re
                  happy to help you get the most out of Chat.co.
                </p>
              </div>
            </div>
          </div>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default TroubleshootingPage;
